import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';
import { ALL_PERMISSION_KEYS } from '@/shared/permissions/catalog';
import { PermissionsContext, type PermissionsContextValue, type SimulatedRole } from './permissionsContextCore';

const SIMULATE_STORAGE_KEY = 'panelclinicas_simulate_role';

declare global {
  interface Window {
    /** Solo DEV/e2e: simular un rol sin pasar por la UI de Roles. */
    __panelSimulateRole?: (role: SimulatedRole | null) => void;
  }
}

function readStoredSimulation(): SimulatedRole | null {
  if (!import.meta.env.DEV || typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(SIMULATE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SimulatedRole>;
    if (!parsed.name || !Array.isArray(parsed.permissionKeys)) return null;
    return { name: parsed.name, permissionKeys: parsed.permissionKeys };
  } catch {
    return null;
  }
}

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user, bootstrapping } = useAuth();
  const [simulating, setSimulating] = useState<SimulatedRole | null>(() => readStoredSimulation());

  const isOwner = user?.role === 'owner';

  const basePermissions = useMemo<string[]>(() => {
    if (user?.permissions) {
      return user.permissions;
    }
    // Fallback defensivo: el owner ve todo aunque /me no haya traído permisos.
    return isOwner ? ALL_PERMISSION_KEYS : [];
  }, [user?.permissions, isOwner]);

  const applySimulation = useCallback((role: SimulatedRole | null) => {
    setSimulating(role);
    if (!import.meta.env.DEV) return;
    if (role) {
      sessionStorage.setItem(SIMULATE_STORAGE_KEY, JSON.stringify(role));
    } else {
      sessionStorage.removeItem(SIMULATE_STORAGE_KEY);
    }
  }, []);

  const simulate = useCallback((role: SimulatedRole) => applySimulation(role), [applySimulation]);
  const stopSimulating = useCallback(() => applySimulation(null), [applySimulation]);

  useEffect(() => {
    if (!bootstrapping && !user) {
      applySimulation(null);
    }
  }, [bootstrapping, user, applySimulation]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.__panelSimulateRole = (role) => applySimulation(role);
    return () => {
      delete window.__panelSimulateRole;
    };
  }, [applySimulation]);

  const value = useMemo<PermissionsContextValue>(() => {
    const permissions = simulating ? simulating.permissionKeys : basePermissions;
    const permissionSet = new Set(permissions);
    return {
      permissions,
      hasPermission: (key: string) => permissionSet.has(key),
      isOwner: Boolean(isOwner),
      isSimulating: simulating !== null,
      simulatingRoleName: simulating?.name ?? null,
      simulate,
      stopSimulating,
    };
  }, [simulating, basePermissions, isOwner, simulate, stopSimulating]);

  return (
    <PermissionsContext.Provider value={value}>
      <div className="flex h-dvh flex-col">
        {user && value.isSimulating && (
          <div
            role="status"
            data-testid="simulation-banner"
            className="z-50 flex min-h-11 shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950"
          >
            <span>{t('permissions.simulating.banner', { role: value.simulatingRoleName })}</span>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              onClick={stopSimulating}
              className="min-h-9 rounded-md bg-amber-950/10 px-3 py-1 text-sm font-semibold hover:bg-amber-950/20"
            >
              {t('permissions.simulating.exit')}
            </button>
          </div>
        )}
        <div className={`min-h-0 flex-1 ${value.isSimulating ? 'overflow-hidden' : ''}`}>{children}</div>
      </div>
    </PermissionsContext.Provider>
  );
}
